import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { listJobs, startJob, stopJob, listConfig, setConfig, JOB_KINDS, SERVICE_TYPES } from '../../services/cloneProvider';
import { getErrorMessage, isConfigTrue } from './badges';
import JobLogPanel from './JobLogPanel';

const KIND_LABEL = { recovery: 'Recovery', health_check: 'Health check', buy: 'Buy' };
const SERVICE_TYPE_LABEL = { fb_ql: 'FB QL', fb_eaaaau: 'FB EAAAAU', fb_eaagno: 'FB EAAGNO' };

export default function JobsTab() {
  const [jobs, setJobs] = useState([]);
  const [productEvalEnabled, setProductEvalEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [expanded, setExpanded] = useState({});
  // Chỉ auto-sync expand/collapse theo trạng thái enabled ở LẦN ĐẦU biết
  // trạng thái mỗi job - sau đó user tự bấm mở/đóng, không bị toggle on/off
  // ghi đè lựa chọn thủ công của họ mỗi lần fetchData() chạy lại.
  const autoSyncedRef = useRef(new Set());

  const fetchData = useCallback(async () => {
    try {
      const [jobList, config] = await Promise.all([listJobs(), listConfig()]);
      setJobs(jobList);
      const peEnabled = isConfigTrue(config.ENABLED_PRODUCT_EVALUATION);
      setProductEvalEnabled(peEnabled);

      setExpanded((prev) => {
        const next = { ...prev };
        for (const job of jobList) {
          const key = `${job.kind}:${job.service_type}`;
          if (!autoSyncedRef.current.has(key)) {
            next[key] = job.enabled;
            autoSyncedRef.current.add(key);
          }
        }
        if (!autoSyncedRef.current.has('product_evaluation')) {
          next.product_evaluation = peEnabled;
          autoSyncedRef.current.add('product_evaluation');
        }
        return next;
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  async function handleToggle(kind, serviceType, enabled) {
    const key = `${kind}:${serviceType}`;
    setBusyKey(key);
    try {
      if (enabled) await stopJob(kind, serviceType);
      else await startJob(kind, serviceType);
      setExpanded((prev) => ({ ...prev, [key]: !enabled }));
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleToggleProductEvaluation() {
    setBusyKey('product_evaluation');
    try {
      await setConfig('ENABLED_PRODUCT_EVALUATION', String(!productEvalEnabled));
      setExpanded((prev) => ({ ...prev, product_evaluation: !productEvalEnabled }));
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setBusyKey(null);
    }
  }

  function findJob(kind, serviceType) {
    return jobs.find((j) => j.kind === kind && j.service_type === serviceType);
  }

  function toggleExpanded(key) {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  if (isLoading) {
    return <div className="rounded-2xl border border-cyan-100 bg-white p-6 text-center text-sm text-slate-500">Đang tải...</div>;
  }

  return (
    <div className="space-y-4">

      <div className="overflow-hidden rounded-2xl border border-cyan-100 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <th className="px-3 py-2">Kind</th>
              {SERVICE_TYPES.map((st) => (
                <th key={st} className="px-3 py-2 text-center">{SERVICE_TYPE_LABEL[st]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {JOB_KINDS.map((kind) => (
              <tr key={kind} className="border-t border-slate-100">
                <td className="px-3 py-3 font-medium text-slate-900">{KIND_LABEL[kind]}</td>
                {SERVICE_TYPES.map((st) => {
                  const job = findJob(kind, st);
                  const key = `${kind}:${st}`;
                  return (
                    <td key={st} className="px-3 py-3 text-center">
                      <button
                        type="button"
                        disabled={busyKey === key}
                        onClick={() => handleToggle(kind, st, job?.enabled)}
                        className={[
                          'rounded-full px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40',
                          job?.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500',
                        ].join(' ')}
                      >
                        {job?.enabled ? 'Đang bật' : 'Đang tắt'}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="border-t border-slate-100">
              <td className="px-3 py-3 font-medium text-slate-900">Product evaluation</td>
              <td colSpan={SERVICE_TYPES.length} className="px-3 py-3 text-center">
                <button
                  type="button"
                  disabled={busyKey === 'product_evaluation'}
                  onClick={handleToggleProductEvaluation}
                  className={[
                    'rounded-full px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40',
                    productEvalEnabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500',
                  ].join(' ')}
                >
                  {productEvalEnabled ? 'Đang bật' : 'Đang tắt'}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-900">Logs</h3>
        {JOB_KINDS.flatMap((kind) =>
          SERVICE_TYPES.map((st) => {
            const key = `${kind}:${st}`;
            return (
              <JobLogPanel
                key={key}
                title={`${KIND_LABEL[kind]} / ${SERVICE_TYPE_LABEL[st]}`}
                kind={kind}
                serviceType={st}
                expanded={Boolean(expanded[key])}
                onToggleExpanded={() => toggleExpanded(key)}
              />
            );
          })
        )}
        <JobLogPanel
          title="Product evaluation"
          systemName="product_evaluation_loop"
          expanded={Boolean(expanded.product_evaluation)}
          onToggleExpanded={() => toggleExpanded('product_evaluation')}
        />
      </div>
    </div>
  );
}
